import { useCallback, useEffect, useState } from "react";

import { ApiError } from "../api/client.js";
import type { Cluster } from "../components/ClusterCard.js";
import {
  EmissionBarChart,
  formatMass,
  type BreakdownRow,
} from "../components/EmissionBarChart.js";
import { Icon } from "../components/Icon.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * La pagina EMISSIONI, portata da `Emissions.qml`.
 *
 * Due modi di guardare gli stessi dati, e il selettore in alto dice quale:
 *
 *  - **tutti gli impianti**: il totale del sito e una tabella per impianto.
 *    Cliccando una riga si scende su quell'impianto, che e' il gesto naturale
 *    quando un numero salta all'occhio.
 *  - **un impianto solo**: il totale, il grafico a barre per sotto-periodo, e
 *    le quattro cifre sulla qualita' del dato.
 *
 * Le cifre sulla qualita' non sono un di piu' burocratico: un totale su una
 * copertura del 30% e un totale su una copertura del 95% sono due
 * affermazioni molto diverse, e senza quelle quattro voci sembrano la stessa.
 * Per lo stesso motivo il totale di un periodo non ancora finito si dichiara
 * PARZIALE.
 */

type Preset = "24h" | "7d" | "30d" | "1y";

const PRESETS: { key: Preset; label: string; period: string }[] = [
  { key: "24h", label: "emissions.last24h", period: "day" },
  { key: "7d", label: "emissions.last7d", period: "week" },
  { key: "30d", label: "emissions.last30d", period: "month" },
  { key: "1y", label: "emissions.lastYear", period: "year" },
];

interface Quality {
  totalHours: number;
  completeHours: number;
  incompleteHours: number;
  gapHours: number;
  overallCoveragePct: number;
}

interface ClusterRow {
  clusterId: string;
  clusterName: string;
  totalMassKg: number;
  uncertaintyPct: number;
  qualityClass: string;
  quality: Quality;
}

interface ViewData {
  isAll: boolean;
  totalMassKg: number;
  uncertaintyPct: number;
  qualityClass: string;
  isPartial: boolean;
  quality: Quality | null;
  breakdown: BreakdownRow[] | null;
  clusterRows: ClusterRow[] | null;
}

export function Emissions({ onBack }: { onBack?: (() => void) | undefined }) {
  const { t } = useI18n();
  const { api, can } = useSession();

  const [clusters, setClusters] = useState<Cluster[]>([]);
  /** "" = tutti gli impianti. */
  const [clusterId, setClusterId] = useState("");
  const [preset, setPreset] = useState<Preset>("30d");
  const [data, setData] = useState<ViewData | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    void api
      .call<Cluster[]>("clusters.list")
      .then(setClusters)
      .catch(() => {
        /* la pagina lo dice con "nessun impianto" */
      });
  }, [api]);

  const load = useCallback(async () => {
    const period = PRESETS.find((p) => p.key === preset)?.period ?? "month";
    const date = new Date().toISOString();
    try {
      if (!clusterId) {
        const b = await api.call<{
          isPartial: boolean;
          grandTotal: { totalMassKg: number; uncertaintyPct: number; qualityClass: string };
          clusters: ClusterRow[];
        }>("emissions.summary", { period, date });
        setData({
          isAll: true,
          totalMassKg: Number(b.grandTotal?.totalMassKg ?? 0),
          uncertaintyPct: Number(b.grandTotal?.uncertaintyPct ?? 40),
          qualityClass: b.grandTotal?.qualityClass ?? "Poor",
          isPartial: b.isPartial ?? false,
          quality: null,
          breakdown: null,
          clusterRows: b.clusters ?? [],
        });
      } else {
        const b = await api.call<{
          isPartial: boolean;
          summary: { totalMassKg: number; uncertaintyPct: number; qualityClass: string };
          quality: Quality;
          breakdown: BreakdownRow[];
        }>("emissions.report", { clusterId, period, date });
        setData({
          isAll: false,
          totalMassKg: Number(b.summary?.totalMassKg ?? 0),
          uncertaintyPct: Number(b.summary?.uncertaintyPct ?? 40),
          qualityClass: b.summary?.qualityClass ?? "Poor",
          isPartial: b.isPartial ?? false,
          quality: b.quality ?? null,
          breakdown: b.breakdown ?? [],
          clusterRows: null,
        });
      }
      setError("");
    } catch (e) {
      setData(null);
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [api, clusterId, preset]);

  useEffect(() => void load(), [load]);

  if (!can("viewEmissions")) {
    return <p className="muted">{t("access.restricted")}</p>;
  }

  const selected = clusters.find((c) => c.id === clusterId);

  return (
    <div className="emissions">
      {/* L'intestazione di `Emissions.qml`: freccia indietro, titolo, e a
          destra selettore, periodi ed esportazione. La freccia c'e' perche'
          nel `.qml` questa e' una pagina che si apre sopra la griglia e da cui
          si torna — non una scheda fra le altre. */}
      <header className="emissions__head">
        {onBack && (
          <button className="settings__back" onClick={onBack} title={t("nav.gridView")}>
            <Icon name="arrow-left" size={20} />
          </button>
        )}
        <h2>
          <Icon name="bar-chart-3" size={20} />
          {t("emissions.title")}
        </h2>

        <span className="emissions__spacer" />

        <select value={clusterId} onChange={(e) => setClusterId(e.target.value)}>
          <option value="">{t("emissions.allClusters")}</option>
          {clusters.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <div className="presets">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              className={`preset${preset === p.key ? " preset--on" : ""}`}
              onClick={() => setPreset(p.key)}
            >
              {t(p.label)}
            </button>
          ))}
        </div>

        {/* L'esportazione in JSON e' il pacchetto OGMP livello 4: totale,
            qualita' del dato e dettaglio per sotto-periodo, con l'anagrafica
            dell'impianto. Lo genera il browser dai dati che ha gia' — non e'
            un secondo calcolo, e' lo stesso oggetto scritto su un file. */}
        {selected && data?.breakdown && can("exportReports") && (
          <button className="emissions__export" onClick={() => exportJson(selected, data, preset)}>
            <Icon name="download" size={14} />
            {t("emissions.exportJson")}
          </button>
        )}
      </header>

      {/* Il contenuto sta in 1400 px centrati, come il `ColumnLayout` del
          `.qml`: a tutta larghezza il totale in cima e la pastiglia della
          qualita' finiscono ai due bordi opposti dello schermo. */}
      <div className="emissions__body">
      {error && <p className="alert">{error}</p>}

      {!data && (
        <p className="muted emissions__none">
          {clusters.length === 0 ? t("emissions.noCluster") : t("emissions.noData")}
        </p>
      )}

      {data && (
        <>
          <section className="hero">
            <div>
              <div className="hero__label">{t("emissions.totalEmissions").toUpperCase()}</div>
              <div className="hero__value">{formatMass(data.totalMassKg)}</div>
              <div className="hero__sub">
                ±{data.uncertaintyPct.toFixed(1)}% {t("emissions.uncertainty").toLowerCase()}
              </div>
            </div>
            <div className="hero__right">
              <span className={`qual qual--${data.qualityClass.toLowerCase()}`}>
                {t(`emissions.quality.${data.qualityClass.toLowerCase()}`)}
              </span>
              {data.quality && (
                <span className="muted small">
                  {t("emissions.coverage")}: {data.quality.overallCoveragePct.toFixed(0)}%
                </span>
              )}
              {/* Un totale parziale letto come definitivo e' un errore che si
                  scopre solo a consuntivo. */}
              {data.isPartial && <span className="qual qual--partial">{t("emissions.partial")}</span>}
            </div>
          </section>

          {!data.isAll && data.breakdown && data.breakdown.length > 0 && (
            <section className="panel">
              <h3>
                {t("emissions.totalEmissions")} — {preset.toUpperCase()}
              </h3>
              <div className="emissions__chart">
                <EmissionBarChart breakdown={data.breakdown} />
              </div>
            </section>
          )}

          {data.isAll && data.clusterRows && data.clusterRows.length > 0 && (
            <section className="panel panel--flush">
              <table className="table emissions__table">
                <thead>
                  <tr>
                    <th>{t("sitemap.cluster")}</th>
                    <th className="ta-right">{t("emissions.totalEmissions")}</th>
                    <th className="ta-right">{t("emissions.uncertainty")}</th>
                    <th className="ta-right">{t("emissions.coverage")}</th>
                    <th className="ta-center">QUAL</th>
                  </tr>
                </thead>
                <tbody>
                  {data.clusterRows.map((r) => (
                    <tr key={r.clusterId} onClick={() => setClusterId(r.clusterId)}>
                      <td className="emissions__name">{r.clusterName || "—"}</td>
                      <td className="ta-right mono">{formatMass(r.totalMassKg)}</td>
                      <td className="ta-right mono">±{r.uncertaintyPct.toFixed(1)}%</td>
                      <td className="ta-right mono">
                        {r.quality ? `${r.quality.overallCoveragePct.toFixed(0)}%` : "—"}
                      </td>
                      <td className="ta-center">
                        <span className={`qual qual--${(r.qualityClass || "poor").toLowerCase()}`}>
                          {t(`emissions.quality.${(r.qualityClass || "poor").toLowerCase()}`)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {data.quality && (
            <div className="qualrow">
              <QualityPill label={t("emissions.totalHours")} value={String(data.quality.totalHours)} />
              <QualityPill
                label={t("emissions.completeHours")}
                value={String(data.quality.completeHours)}
                tone="normal"
              />
              <QualityPill
                label={t("emissions.gapHours")}
                value={String(data.quality.gapHours)}
                tone="warning"
              />
              <QualityPill
                label={t("emissions.coverage")}
                value={`${data.quality.overallCoveragePct.toFixed(0)}%`}
              />
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}

function QualityPill({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "normal" | "warning";
}) {
  return (
    <span className="qpill">
      <span className="qpill__label">{label}</span>
      <b className={tone ? `qpill__value--${tone}` : undefined}>{value}</b>
    </span>
  );
}

/** Il pacchetto OGMP livello 4, salvato dal browser. Nell'app desktop e' un
 *  dialogo nativo di salvataggio; qui il file lo scarica il browser, che e'
 *  l'unico modo che ha di scrivere su disco. */
function exportJson(cluster: Cluster, data: ViewData, preset: Preset) {
  const period = PRESETS.find((p) => p.key === preset)?.period ?? "month";
  const date = new Date().toISOString();
  const payload = {
    ogmpLevel: 4,
    generatedAt: date,
    facility: {
      clusterName: cluster.name,
      areaCode: cluster.areaCode ?? null,
      sensorIp: cluster.sensorIp ?? null,
      latitude: null,
      longitude: null,
    },
    report: {
      clusterId: cluster.id,
      period,
      date,
      summary: {
        totalMassKg: data.totalMassKg,
        uncertaintyPct: data.uncertaintyPct,
        qualityClass: data.qualityClass,
      },
      quality: data.quality,
      breakdown: data.breakdown,
    },
  };
  const safe = (cluster.name || "cluster").replace(/[^a-zA-Z0-9_-]/g, "_");
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `emissions_${safe}_${period}_${date.slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}
